import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import { CurrencyToggle } from '../components/CurrencyToggle';
import {
  LayoutDashboard,
  Building2,
  ArrowLeftRight,
  ClipboardList,
  Users,
  Menu,
  ChevronDown,
  MessageSquare,
  AlertTriangle,
  Key,
  Book,
  FlaskConical,
  Search,
  Bell,
  Settings,
  User,
  FileText,
  Sun,
  Moon,
  LogOut,
  Smartphone,
  TestTube2
} from 'lucide-react';

export const AdminLayout = () => {
  const { user, admin, logout } = useAdminAuth();
  const { isDarkMode, toggleTheme } = useAdminTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // All menu items
  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Merchants', href: '/admin/merchants', icon: Building2 },
    { name: 'Transactions', href: '/admin/transactions', icon: ArrowLeftRight },
    { name: 'SIMVault Monitor', href: '/admin/simvault', icon: Smartphone },
    { name: 'Payment Simulator', href: '/admin/payment-simulator', icon: TestTube2 },
    { name: 'SMS Inbox', href: '/admin/sms-inbox', icon: MessageSquare },
    { name: 'SMS Review', href: '/admin/sms-review', icon: AlertTriangle },
    { name: 'Test SMS', href: '/admin/test-sms', icon: FlaskConical },
    { name: 'Webhook Keys', href: '/admin/webhook-keys', icon: Key },
    { name: 'Integration Guide', href: '/admin/integration-guide', icon: Book },
    { name: 'Audit Logs', href: '/admin/audit-logs', icon: ClipboardList },
    { name: 'Admin Users', href: '/admin/users', icon: Users },
    { name: 'Reports', href: '/admin/reports', icon: FileText },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const userName = user?.name || admin?.name || 'Test Admin';
  const userEmail = user?.email || 'admin@test.com';
  const userRole = admin?.role || 'Super Admin';

  return (
    <div 
      className={`h-screen w-screen overflow-hidden flex transition-colors duration-300 ${
        isDarkMode ? 'bg-slate-900' : 'bg-[#F5F7FA]'
      }`}
      style={{ fontFamily: 'Inter, sans-serif' }}
      data-testid="admin-layout"
    >
      {/* LEFT SIDEBAR */}
      <aside 
        className="flex flex-col flex-shrink-0 transition-all duration-300"
        style={{ 
          width: '220px',
          background: isDarkMode 
            ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
            : 'linear-gradient(180deg, #1E3A5F 0%, #162D4A 50%, #122640 100%)',
        }}
      >
        {/* Title */}
        <div className="px-5 py-5 flex items-start justify-between">
          <div>
            <h1 className="text-white font-semibold text-base">TrustBridge</h1>
            <p className="text-blue-400 text-sm">Admin Panel</p>
          </div>
          <button 
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-all duration-300 hover:scale-110 ${
              isDarkMode 
                ? 'text-yellow-400 hover:bg-white/10' 
                : 'text-slate-300 hover:bg-white/10'
            }`}
            data-testid="theme-toggle"
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 overflow-y-auto custom-scrollbar">
          <div className="space-y-0.5">
            {navigation.map((item, index) => {
              const active = isActive(item.href);
              const IconComponent = item.icon;
              
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-300 text-sm group relative overflow-hidden ${
                    active 
                      ? 'bg-teal-500/20 text-white' 
                      : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                  }`}
                  style={{ 
                    animationDelay: `${index * 30}ms`,
                    transform: 'translateX(0)',
                  }}
                  data-testid={`admin-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <IconComponent 
                    className={`w-[18px] h-[18px] transition-all duration-300 ${
                      active ? 'text-teal-400' : 'group-hover:scale-110 group-hover:text-teal-400'
                    }`} 
                    strokeWidth={1.8} 
                  />
                  <span className={`font-medium transition-transform duration-300 ${!active && 'group-hover:translate-x-1'}`}>
                    {item.name}
                  </span>
                  {active && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
                  )}
                  {/* Hover gradient effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r from-teal-500/0 via-teal-500/10 to-teal-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${active && 'hidden'}`} />
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Bottom - Profile Card & Logout */}
        <div className="px-4 py-4 border-t border-white/10">
          {/* Profile Card - Clickable */}
          <div 
            onClick={() => navigate('/admin/profile')}
            className="flex items-center gap-3 mb-4 p-2 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
          >
            <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{userName}</p>
              <p className="text-blue-400 text-xs">{userRole}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400 -rotate-90 transition-transform duration-300 group-hover:translate-x-1" />
          </div>
          
          {/* Logout Button */}
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-300 active:scale-95 group"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <header 
          className={`flex items-center justify-between px-5 py-3 border-b transition-colors duration-300 ${
            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
          }`}
        >
          <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Admin Panel
          </h2>
          <div className="flex items-center gap-4">
            {/* Currency Toggle */}
            <CurrencyToggle isDarkMode={isDarkMode} />
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-slate-700 text-yellow-400 hover:bg-slate-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="theme-toggle"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </header>
        
        {/* Content */}
        <main className={`flex-1 overflow-auto p-5 transition-colors duration-300 ${
          isDarkMode ? 'bg-slate-900' : 'bg-gray-50'
        }`}>
          <div className="animate-fadeIn">
            <Outlet context={{ isDarkMode }} />
          </div>
        </main>
      </div>

      {/* Custom Styles & Global Animations */}
      <style>{`
        /* Fade In Animation */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(20, 184, 166, 0); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.4s ease-out forwards;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out forwards;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out forwards;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .animate-bounce-subtle {
          animation: bounce-subtle 0.5s ease-in-out;
        }
        
        /* Interactive Card Styles */
        .interactive-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .interactive-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px -8px rgba(0, 0, 0, 0.15);
        }
        
        .interactive-card:active {
          transform: translateY(-2px) scale(0.98);
        }
        
        /* Button Click Effect */
        .btn-click {
          transition: all 0.15s ease;
        }
        
        .btn-click:active {
          transform: scale(0.95);
        }
        
        /* Table Row Hover */
        .table-row-hover {
          transition: all 0.2s ease;
        }
        
        .table-row-hover:hover {
          background: linear-gradient(90deg, transparent 0%, rgba(20, 184, 166, 0.05) 50%, transparent 100%);
        }
        
        /* Icon Hover Effects */
        .icon-hover {
          transition: all 0.2s ease;
        }
        
        .icon-hover:hover {
          transform: scale(1.15) rotate(5deg);
        }
        
        /* Staggered Animation Delays */
        .stagger-1 { animation-delay: 50ms; }
        .stagger-2 { animation-delay: 100ms; }
        .stagger-3 { animation-delay: 150ms; }
        .stagger-4 { animation-delay: 200ms; }
        .stagger-5 { animation-delay: 250ms; }
        
        /* Scrollbar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 2px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
        
        /* Ripple Effect */
        .ripple {
          position: relative;
          overflow: hidden;
        }
        
        .ripple::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }
        
        .ripple:active::after {
          width: 300px;
          height: 300px;
        }
        
        /* Glow effect on focus */
        .focus-glow:focus {
          box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.3);
        }
        
        /* Gradient text */
        .gradient-text {
          background: linear-gradient(135deg, #14b8a6 0%, #06b6d4 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;
